/**
 * Calculate the average value from an array of values
 *
 * @param {Array} arr
 *
 * @returns {Number}
 */
export function avg (arr) {
  return arr.reduce(function (tot, val) {
    return tot + val;
  }, 0) / arr.length;
}

/**
 * Calculate the maximum value in an array
 *
 * @param  {Array} arr
 * @return {Number}
 */
export function max (arr) {
  return arr.reduce(function (tot, val) {
    return Math.max(tot, val);
  }, 0);
}

/**
 * Calculate the minimum value in an array
 *
 * @param  {Array} arr
 * @return {Number}
 */
export function min (arr) {
  return arr.reduce(function (tot, val) {
    return Math.min(tot, val);
  }, 0);
}

export function minmax (arr) {
  return arr.reduce(function (minmax, val) {
    if (typeof minmax[0] === 'undefined') {
      minmax[0] = val;
    } else {
      minmax[0] = Math.min(minmax[0], val);
    }

    if (typeof minmax[1] === 'undefined') {
      minmax[1] = val;
    } else {
      minmax[1] = Math.max(minmax[1], val);
    }

    return minmax;
  }, []);
}

export function sample (data, desiredLength) {
  const dataLen = data.length;
  const pointsPerBar = dataLen / desiredLength;

  // resample the waveform data to fit the number of bars we need
  const sampled = [];
  for (let i = 0; i < desiredLength; i++) {
    const start = Math.floor(i * pointsPerBar);
    const end = Math.floor(start + pointsPerBar);
    if (start === end) {
      sampled.push(data[start]);
    } else {
      sampled.push(avg(data.slice(start, end)));
    }
  }

  return sampled;
}

export const interpolate = (function () {
  function pchip (x, y, xNew) {
    const n = x.length;
    const h = new Array(n - 1);
    const d = new Array(n - 1);

    // Compute the spacing and the slopes
    for (let i = 0; i < n - 1; i++) {
      h[i] = x[i + 1] - x[i];
      d[i] = (y[i + 1] - y[i]) / h[i];
    }

    // Compute the tangents
    const m = new Array(n);
    m[0] = d[0];
    for (let i = 1; i < n - 1; i++) {
      m[i] = (d[i - 1] + d[i]) / 2;
    }
    m[n - 1] = d[n - 2];

    // Perform the interpolation
    const yNew = new Array(xNew.length);
    for (let j = 0; j < xNew.length; j++) {
      const xVal = xNew[j];
      // Find the right interval for xNew[j]
      let i = 0;
      while (i < n - 1 && x[i + 1] < xVal) {
        i++;
      }
      // If xVal is out of bounds, use the endpoint values
      if (i === 0) {
        yNew[j] = y[0];
      } else if (i === n - 1) {
        yNew[j] = y[n - 1];
      } else {
        const t = (xVal - x[i]) / h[i];
        const a = y[i];
        const b = m[i];
        const c = (3 * (y[i + 1] - a) / h[i] - 2 * b - m[i]) / h[i];
        const d = (b + m[i + 1] - 2 * (y[i + 1] - a) / h[i]) / (h[i] * h[i]);
        // Calculate the interpolated value
        yNew[j] = a + b * t + c * t * t + d * t * t * t;
      }
    }
    return yNew;
  }

  // Example usage of PCHIP for upsampling and downsampling
  return function resampleUsingPCHIP (yValues, targetLength) {
    // Generate corresponding x values
    const xValues = yValues.map((_, index) => index); // x = [0, 1, 2, ..., length-1]
    const targetX = new Array(targetLength).fill(0).map((_, i) => i * (yValues.length - 1) / (targetLength - 1));

    return pchip(xValues, yValues, targetX);
  };
})();

/**
 * Normalize a value to a range
 *
 * @param {Number} val The value to be normalized
 * @param {Array}  r1  The min and max range of the original value
 * @param {Array}  r2  The min and max range of the target value
 *
 * @returns {Number}
 */
export function normalize (val, r1, r2 = [0, 1]) {
  return ((val - r1[0]) / (r1[1] - r1[0])) * (r2[1] - r2[0]) + r2[0];
}

/**
 * Round a number to the nearest decimal place
 *
 * @param {Number} value     The number to be rounded
 * @param {Number} precision The number of decimal places to round to
 * @param {Number}
 */
export function round (value, precision = 3) {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}

export function roundNearest (value, multiple = 1) {
  return Math.round(value * multiple) / multiple;
}

/**
 * Round a number down to the nearest decimal place
 *
 * @param {Number} value     The number to be rounded
 * @param {Number} precision The number of decimal places to round to
 * @param {Number}
 */
export function floor (value, precision = 3) {
  const multiplier = Math.pow(10, precision);
  return Math.floor(value * multiplier) / multiplier;
}
